import { useEffect, useState } from "react";
import { logout } from "../api.js";
import { Applications } from "./Applications.js";
import { ApplicationDetail } from "./ApplicationDetail.js";
import { EditFlow } from "../EditFlow.js";
import { usePortalLookups } from "./lookups.js";
import { Landing } from "./Landing.js";
import { MyData } from "./MyData.js";
import { PortalShell, type PortalTab } from "./PortalShell.js";
import { ProfileScreen } from "./ProfileScreen.js";
import { useBranding } from "./branding.js";
import { fetchProfile } from "./api.js";
import { Skeleton } from "./ui.js";

/**
 * The applicant portal — served on the `id.` host.
 *
 * Screens: the signed-out landing page, my applications, one application, my profile, my data.
 * Routing is a state machine rather than a router library, because five screens do not justify
 * shipping one into an app that shares the form's 150 KB budget.
 */

type Screen = "signin" | PortalTab | "application" | "edit";

export const PortalApp = (): JSX.Element => {
  const [screen, setScreen] = useState<Screen>("signin");
  const [email, setEmail] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const branding = useBranding();
  const lookups = usePortalLookups();

  // A respondent session is an httpOnly cookie, so the only way to know whether one is live is to
  // ask for something that needs it. A 401 simply means "show the landing page".
  useEffect(() => {
    void fetchProfile()
      .then((result) => {
        setEmail(result.respondent.email);
        setScreen("applications");
      })
      .catch(() => setScreen("signin"))
      .finally(() => setChecking(false));
  }, []);

  const signOut = async (): Promise<void> => {
    await logout().catch(() => undefined);
    setEmail(null);
    setOpenId(null);
    setScreen("signin");
  };

  // One frame of "is anyone signed in" — a skeleton of the chrome rather than the word "Loading",
  // so a returning applicant does not see the landing page flash before their applications land.
  if (checking) {
    return (
      <div className="sga-page">
        <div className="sga-header">
          <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>
        <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6" aria-busy="true">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="mt-6 h-24 w-full" />
        </main>
      </div>
    );
  }

  if (screen === "signin") {
    return (
      <Landing
        branding={branding}
        onSignedIn={(signedInEmail) => {
          setEmail(signedInEmail);
          setScreen("applications");
        }}
      />
    );
  }

  return (
    <PortalShell
      branding={branding}
      email={email}
      // Both the detail screen and the edit screen live under Applications, so that tab stays lit.
      active={screen === "application" || screen === "edit" ? "applications" : screen}
      onNavigate={setScreen}
      onSignOut={() => void signOut()}
    >
      {screen === "applications" && (
        <Applications
          onOpen={(id) => {
            setOpenId(id);
            setScreen("application");
          }}
          onEdit={(id) => {
            setOpenId(id);
            setScreen("edit");
          }}
        />
      )}

      {screen === "edit" && openId && (
        // The same screen the form link opens, so the two cannot disagree about what may change.
        <EditFlow
          submissionId={openId}
          colors={branding.colors}
          branding={{
            headingFont: branding.fonts.heading,
            bodyFont: branding.fonts.body,
          }}
          lookups={lookups}
          onDone={() => setScreen("applications")}
          onCancel={() => setScreen("applications")}
        />
      )}
      {screen === "application" && openId && (
        <ApplicationDetail id={openId} onBack={() => setScreen("applications")} />
      )}
      {screen === "profile" && <ProfileScreen />}
      {screen === "data" && <MyData />}
    </PortalShell>
  );
};
